/*
 * Module for the learner results area
 */

define([
  "marionette",
  "jquery",
  "underscore",
  "configuration/properties",
  "messaging/messenger",
  "modules/accountAdmin/kbAdmin/kbModel",
  "modules/learner/learnerResults/learnerResultsView",
  "modules/shared/sortableList/sortableListModule",
  "modules/learner/learnerTranscriptModal/learnerTranscriptModalModule",
  "modules/learner/learnerModel",
], function (
  Marionette,
  $,
  _,
  Properties,
  Messenger,
  KbModel,
  LearnerResultsView,
  SortableListModule,
  LearnerTranscriptModalModule,
  LearnerModel,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "learnerResults";
      this.region = options.region;
      this._setUpVents();
      this._setUpReqres();
    },
    show: function (resultsCollection, isClustered, matchType, localeId) {
      this.isClustered = isClustered;
      this.matchType = matchType;
      this.resultsCollection = resultsCollection;
      //get sortable list headers
      //pass the results collection because we will use it to help determine any conversation attribute header
      // columns
      const headers = Messenger.reqres.request(
        "learner:results:headers",
        isClustered,
        matchType,
        resultsCollection,
        localeId,
      );

      this.layout = new LearnerResultsView.learnerResultsLayout();
      //initialize sub modules
      this.list = this.addSubModule(
        new SortableListModule({
          region: this.layout.list,
          title: " ",
          downloadable: true,
          downloadID: "downloadLearner",
          headers: headers,
          recordsPerPage: SortableListModule.DEFAULT_RECORDS_PER_PAGE,
          namespace: this.name,
        }),
      );

      //Switch to the topics page and show the mini-learner
      this.layout.on(
        "show:topics:page",
        function () {
          Messenger.vent.trigger("show:topics");
          Messenger.vent.trigger("show:mini:learner", true); //expand the mini-learner (expand = true)
        },
        this,
      );

      //event for handling opening the status menu
      this.layout.on(
        "open:transcript:status:menu",
        function () {
          //clear any old errors
          this.clearError();
          const selectedTranscripts = Messenger.reqres.request("learnerResults:checked:models");
          if (selectedTranscripts.length > 0) {
            const statuses = Messenger.reqres.request("kb:mart:statuses").clone(); //clone the statuses collection
            // because we will remove a model
            //remove the status that the selectedTranscripts have (they all have the same status, so just check the
            // status of the 1st one)
            statuses.remove(statuses.findWhere({ id: selectedTranscripts[0].get("authStatusId") }));
            this.layout.setStatusMenu(statuses, selectedTranscripts);
          } else {
            this.controlsError($.t("learner.results.emptyChangeStatus"));
          }
        },
        this,
      );

      //Changes the status of each selected transcript and saves the change to the server
      this.layout.on(
        "change:transcript:status",
        function (transcripts, statusId) {
          const transcriptsUpdateModel = resultsCollection.updateStatuses(statusId, transcripts);
          this._deleteClusteringJobs(transcriptsUpdateModel);
        },
        this,
      );

      //Delete all selected transcripts
      this.layout.on(
        "delete:transcripts",
        function () {
          //clear any old errors
          this.clearError();
          //get the checked models from the sortable list (with namespace)
          const transcriptsToDelete = Messenger.reqres.request("learnerResults:checked:models");
          if (transcriptsToDelete.length > 0) {
            Messenger.yesNoDialog(
              "learner.results.dialog.deleteTranscripts",
              {
                yes: function () {
                  Messenger.vent.trigger("track:interaction", {
                    action: "Delete Learner Transcripts",
                    itemCount: transcriptsToDelete.length,
                    object: "Learner Transcripts",
                  });
                  const transcriptsUpdateModel =
                    resultsCollection.deleteModels(transcriptsToDelete);
                  this._deleteClusteringJobs(transcriptsUpdateModel);
                },
              },
              this,
            );
          } else {
            this.controlsError($.t("learner.results.emptyDelete"));
          }
        },
        this,
      );

      //This code needs to be changed because it is referring to elements in LearnerConfig
      const settings = Messenger.reqres.request("learner:config:settings");
      Messenger.reqres.setHandler(
        "downloadUrl:downloadLearner",
        function () {
          return settings;
        },
        this,
      );
      const i18nParameters = {};
      i18nParameters.downloadableTitle = $.t("learner.downloadTitle");
      i18nParameters.none = $.t("common.none");
      Messenger.reqres.setHandler(
        "report:i18nHeaders:downloadLearner",
        function () {
          //Finish it up with Topic Match info
          const completeI18NParameters = i18nParameters;
          const allVisibleColumns = Messenger.reqres.request("learnerResults:all:visible:columns");

          for (let i = 0; i < allVisibleColumns.length; i++) {
            const column = allVisibleColumns[i];
            const attribute = column.get("modelAttribute");
            //First, cover the ones that we do have translations for.  After that we'll do custom ones.
            if (attribute === "userUtterance") {
              //oh snap.  there are two of these.  to figure out which one it is, we must determine if this is a
              // cluster
              if (settings.cluster) {
                completeI18NParameters.utterance = $.t("learner.headers.clusteredQuestion");
              } else {
                completeI18NParameters.utterance = $.t("learner.headers.question");
              }
            } else if (attribute === "time") {
              completeI18NParameters.timestamp = $.t("learner.headers.date");
            } else if (attribute === "count") {
              completeI18NParameters.count = $.t("learner.headers.count");
            } else if (attribute === "topicMatchNames") {
              //This one can also vary.
              if (Number(settings.matchType) === 0) {
                completeI18NParameters.topicMatches = $.t("learner.headers.nearMiss");
              } else {
                completeI18NParameters.topicMatches = $.t("learner.headers.topicMatches");
              }
            } else if (attribute === "agentName") {
              completeI18NParameters.agentName = $.t("learner.headers.agentName");
            } else if (attribute === "caseId") {
              completeI18NParameters.caseId = $.t("learner.headers.caseId");
            } else if (attribute === "touchPointName") {
              completeI18NParameters.touchPointName = $.t("learner.headers.touchPointName");
            } else if (attribute === "touchPointId") {
              completeI18NParameters.touchPointId = $.t("learner.headers.touchPointId");
            } else if (attribute === "localeName") {
              completeI18NParameters.localeName = $.t("learner.headers.localeName");
            } else if (attribute === "localeId") {
              completeI18NParameters.localeId = $.t("learner.headers.localeId");
            } else {
              //custom one.  Indicate that it's a column header parameter via _col_ to start the name
              completeI18NParameters[attribute] = attribute;
            }
          }
          return completeI18NParameters;
        },
        this,
      );

      this.layout.on(
        "show",
        function () {
          resultsCollection.onSynced(function () {
            this.layout.setResultsCount(resultsCollection.length);
          }, this);
          this._showSubViews(resultsCollection);
        },
        this,
      );
      this.showRegion(this.layout);
    },
    clearError: function () {
      this.layout.clearError();
    },
    controlsError: function (errorMessage) {
      this.layout.controlsError(errorMessage);
    },
    _deleteClusteringJobs: async function (transcriptsUpdateModel) {
      const kb = Messenger.reqres.request("active:kb:model");
      if (!this.isClustered || kb.get("engineMode") !== KbModel.ENGINE_MODE.SEMANTIC) {
        return;
      }
      if (transcriptsUpdateModel) {
        await transcriptsUpdateModel.onSyncedPromise();
        await this._deleteJobs();
      } else {
        await this._deleteJobs();
      }
    },
    _deleteJobs: async function () {
      const job = new LearnerModel.clusteringJob();
      try {
        const jobDeleteModel = job.deleteAllJobsForUser();
        await jobDeleteModel.onSyncedPromise();
      } catch (e) {
        console.error("Could not delete past clustering jobs", e);
        Messenger.okDialog($.t("learner.results.job.deleteError"));
      }
    },
    _showSubViews: function (resultsCollection) {
      this.list.show(resultsCollection);
    },
    _setUpReqres: function () {
      //reqres to get the current learner results (used by mini-learner)
      Messenger.reqres.setHandler(
        "learner:results",
        function () {
          return this.resultsCollection;
        },
        this,
      );
      //reqres to get the learner match type
      Messenger.reqres.setHandler(
        "learner:results:match:type",
        function () {
          return this.matchType;
        },
        this,
      );
    },
    _setUpVents: function () {
      //Show the results when we are passed the results from the config area
      //also passes a boolean for whether the collection is clustered, and a number describing the match type
      Messenger.vent.on(
        "show:learner:results",
        function (resultsCollection, isClustered, matchType, localeId) {
          this.show(resultsCollection, isClustered, matchType, localeId);
          //show the mini learner, if it's open (or set it to be open when we view the topics page)
          Messenger.vent.trigger("show:mini:learner");
        },
        this,
      );

      Messenger.vent.on("delete:clustering:jobs", this._deleteClusteringJobs, this);

      //Show learner transcript modal (or clustered modal) when one of the records is clicked in the sortableList
      Messenger.vent.on(
        "learnerResults:selected:row",
        function (transcript) {
          //determine the title used based on whether this is a question or a cluster
          let modalTitle;
          if (this.isClustered) {
            modalTitle = "learner.transcript.clusteredTitle";
          } else {
            modalTitle = "learner.transcript.title";
          }
          Messenger.vent.trigger(
            "show:modal",
            LearnerTranscriptModalModule,
            {
              matchType: this.matchType,
              disableLearnerFeatures: false,
            },
            transcript,
            {
              title: modalTitle,
              closeButton: true,
              closeOnBackgroundClick: true,
              modalHeight: "600px",
              trackInteraction: {
                action: "Show Learner Transcript",
                object: "Learner Transcript",
                source: "Learner",
              },
            },
          );
        },
        this,
      );
    },
  });
});
